import type { Association, ImportFile } from './types';
export const MAX_BYTES = 50 * 1024 * 1024;
export const MAX_FILES = 20;
export async function sha256(bytes: ArrayBuffer) {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}
export function container(
  bytes: ArrayBuffer,
): 'wav' | 'mp3' | 'm4a' | undefined {
  const b = new Uint8Array(bytes);
  const text = (start: number, end: number) =>
    String.fromCharCode(...b.slice(start, end));
  if (text(0, 4) === 'RIFF' && text(8, 12) === 'WAVE') return 'wav';
  if (text(0, 3) === 'ID3' || (b[0] === 255 && (b[1] & 224) === 224))
    return 'mp3';
  if (text(4, 8) === 'ftyp') return 'm4a';
  return undefined;
}
export async function decodeDuration(bytes: ArrayBuffer): Promise<number> {
  const context = new AudioContext();
  try {
    const decoded = await Promise.race([
      context.decodeAudioData(bytes.slice(0)),
      new Promise<never>((_, reject) => {
        const id = setTimeout(
          () => reject(new Error('音频解码超时，请重新选择文件')),
          15000,
        );
        // Cleared below via the shared timeout handle.
        decodeTimers.set(context, id);
      }),
    ]);
    const duration = Math.round(decoded.duration * 1000);
    if (!Number.isFinite(duration) || duration <= 0)
      throw new Error('音频时长无效');
    return duration;
  } finally {
    clearTimeout(decodeTimers.get(context));
    decodeTimers.delete(context);
    await context.close();
  }
}
const decodeTimers = new WeakMap<AudioContext, ReturnType<typeof setTimeout>>();
export async function inspectFiles(
  files: File[],
  base: Association,
  decode = decodeDuration,
): Promise<ImportFile[]> {
  if (files.length > MAX_FILES) throw new Error('每批最多20个文件，请分批选择');
  const result: ImportFile[] = [];
  // Sequential decoding bounds memory use for twenty 50 MiB files.
  for (const file of files) {
    const entry: ImportFile = {
      uuid: crypto.randomUUID(),
      filename: file.name,
      sizeBytes: file.size,
      durationMs: 0,
      sha256: '',
      lastModified: file.lastModified,
      status: 'invalid',
      association: {
        ...base,
        title: file.name.replace(/\.[^.]+$/, ''),
        confirmed: false,
      },
    };
    try {
      if (!file.size) throw new Error('文件为空');
      if (file.size > MAX_BYTES) throw new Error('单文件不能超过50MB');
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!['wav', 'mp3', 'm4a'].includes(ext || ''))
        throw new Error('仅支持WAV、MP3、M4A');
      const bytes = await file.arrayBuffer();
      if (container(bytes) !== ext)
        throw new Error('文件内容与扩展名不符，或容器已损坏');
      entry.durationMs = await decode(bytes);
      if (!Number.isFinite(entry.durationMs) || entry.durationMs <= 0)
        throw new Error('音频时长无效');
      entry.sha256 = await sha256(bytes);
      if (
        file.lastModified > 0 &&
        file.lastModified <= Date.parse(base.startedAt)
      ) {
        entry.association.startedAt = new Date(file.lastModified).toISOString();
      }
      if (/课堂|教学|lesson|classroom/i.test(file.name))
        entry.association.scene = 'classroom';
      else if (/沟通|家访|communication/i.test(file.name))
        entry.association.scene = 'communication';
      entry.blob = file;
      entry.status = 'ready';
    } catch (error) {
      entry.error = error instanceof Error ? error.message : '读取失败';
    }
    result.push(entry);
  }
  return result;
}
/** Deterministic audible PCM sample. This is a generated simulation, never device audio. */
export function sampleWave(seed: number): ArrayBuffer {
  const sampleRate = 8000;
  const samples = sampleRate * 2;
  const bytes = new ArrayBuffer(44 + samples * 2);
  const view = new DataView(bytes);
  const put = (offset: number, value: string) =>
    Array.from(value).forEach((v, i) => {
      view.setUint8(offset + i, v.charCodeAt(0));
    });
  put(0, 'RIFF');
  view.setUint32(4, bytes.byteLength - 8, true);
  put(8, 'WAVE');
  put(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  put(36, 'data');
  view.setUint32(40, samples * 2, true);
  for (let i = 0; i < samples; i++)
    view.setInt16(
      44 + i * 2,
      Math.round(
        1500 * Math.sin((2 * Math.PI * (240 + (seed % 600)) * i) / sampleRate),
      ),
      true,
    );
  return bytes;
}
